import { ArrowRightToLine, ArrowDownToLine, Ungroup, Columns3, Rows3 } from "lucide-react";
import { useState } from "react";
import { OPENINGS, openingLabel, visibleCells, anchorOf, spanAt, mergeCell, splitCell, divideCell, cellAt, cellOrigin } from "@/lib/geometry";
import { ElevationThumb } from "@/components/ElevationThumb";
import { BandsSection } from "@/components/BandBox";
import { JointsBox } from "@/components/JointsBox";
import { GLASS_PRESETS } from "@/lib/typology";
import { infillOf, INFILL_PRESETS } from "@/lib/infill";

const InfillChip = ({ spec }) => {
  const inf = infillOf(spec);
  if (!inf) return null;
  return (
    <span className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] text-slate-300" data-testid="panel-infill-chip">
      <span className="w-3 h-3 rounded-sm border border-slate-500" style={{ background: inf.print }} />Infill: {inf.label} — shaded as a non-glass panel
    </span>
  );
};

const iconItem = (op) => ({ width: 600, height: 700, columns: [600], rows: [700], cells: [[op]], frame_width: 40, mullion_width: 60, sash_width: 40 });

const SpanBtn = ({ testid, onClick, disabled, icon: I, children }) => (
  <button data-testid={testid} onClick={onClick} disabled={disabled}
    className="h-8 px-2 rounded-md border border-slate-700 text-[11px] text-slate-300 flex items-center justify-center gap-1.5 hover:border-cyan-500 hover:text-cyan-300 transition-colors disabled:opacity-30 disabled:pointer-events-none">
    <I size={13} />{children}
  </button>
);

export const PanelsTab = ({ item, update, selected, setSelected }) => {
  const raw = selected && item.cells[selected.r]?.[selected.c] !== undefined ? selected : { r: 0, c: 0 };
  const sel = anchorOf(item, raw.r, raw.c);
  const span = spanAt(item, sel.r, sel.c);
  const current = item.cells[sel.r][sel.c];
  const setOp = (op) => update({ ...item, cells: item.cells.map((row, r) => row.map((v, c) => (r === sel.r && c === sel.c ? op : v))) });
  const [divN, setDivN] = useState(2);
  const keepSel = (next) => { const o = cellOrigin(item, sel.r, sel.c); update(next); setSelected(cellAt(next, o.x, o.y)); };
  const merge = (dir) => keepSel(mergeCell(item, sel.r, sel.c, dir));
  const divide = (axis) => keepSel(divideCell(item, sel.r, sel.c, axis, divN));
  const key = `${sel.r}-${sel.c}`;
  const panelGlass = item.panel_glass?.[key] || "";
  const setPanelGlass = (v) => {
    const pg = { ...(item.panel_glass || {}) };
    if (v) pg[key] = v; else delete pg[key];
    update({ ...item, panel_glass: pg });
  };
  return (
    <div className="space-y-5">
      <BandsSection item={item} update={update} />
      <JointsBox item={item} update={update} />
      <div>
        <p className="mb-2 font-mono text-[10px] uppercase tracking-wider text-slate-400">Panel grid · click to select</p>
        <div className="grid gap-1 p-2 rounded-md bg-[#0B0F19] border border-slate-800"
          style={{ gridTemplateColumns: item.columns.map((w) => `${w}fr`).join(" "), gridAutoRows: "3rem" }}>
          {visibleCells(item).map(({ r, c, rs, cs }) => (
            <button key={`${r}-${c}`} data-testid={`grid-cell-${r}-${c}`} onClick={() => setSelected({ r, c })}
              style={{ gridColumn: `${c + 1} / span ${cs}`, gridRow: `${r + 1} / span ${rs}` }}
              className={`rounded text-[10px] font-mono uppercase border transition-colors ${sel.r === r && sel.c === c ? "border-cyan-400 bg-cyan-500/15 text-cyan-200" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}>
              {OPENINGS.find((o) => o.value === item.cells[r][c])?.code}
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs text-slate-400" data-testid="selected-cell-info">
          Row {sel.r + 1}, Col {sel.c + 1}{(span.rs > 1 || span.cs > 1) && <span className="text-amber-300"> · spans {span.cs} col × {span.rs} row</span>} · <span className="text-cyan-300">{openingLabel(current)}</span>
        </p>
        <div className="mt-3 grid grid-cols-3 gap-2">
          <SpanBtn testid="merge-right-btn" icon={ArrowRightToLine} onClick={() => merge("right")} disabled={sel.c + span.cs >= item.columns.length}>Merge →</SpanBtn>
          <SpanBtn testid="merge-down-btn" icon={ArrowDownToLine} onClick={() => merge("down")} disabled={sel.r + span.rs >= item.rows.length}>Merge ↓</SpanBtn>
          <SpanBtn testid="split-cell-btn" icon={Ungroup} onClick={() => update(splitCell(item, sel.r, sel.c))} disabled={span.rs === 1 && span.cs === 1}>Split</SpanBtn>
        </div>
        <div className="mt-3 rounded-md border border-slate-800 p-2.5" data-testid="divide-panel-box">
          <p className="font-mono text-[10px] uppercase tracking-wider text-slate-400 mb-2">Divide this panel only (fanlight / over panel / side light)</p>
          <div className="grid grid-cols-[72px_1fr_1fr] gap-2">
            <select data-testid="divide-count-select" value={divN} onChange={(e) => setDivN(+e.target.value)}
              className="h-8 rounded-md bg-[#0B0F19] border border-slate-700 px-2 text-xs text-slate-100 focus:outline-none focus:border-cyan-500">
              {[2, 3, 4, 5, 6].map((k) => <option key={k} value={k}>{`${k} parts`}</option>)}
            </select>
            <SpanBtn testid="divide-vertical-btn" icon={Columns3} onClick={() => divide("col")}>Vertical</SpanBtn>
            <SpanBtn testid="divide-horizontal-btn" icon={Rows3} onClick={() => divide("row")}>Horizontal</SpanBtn>
          </div>
        </div>
        <label className="block mt-4">
          <span className="block mb-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-400">Panel glass / infill (blank = side light / overhead / item default)</span>
          <input data-testid="panel-glass-input" list="dl-panel-glass" value={panelGlass} onChange={(e) => setPanelGlass(e.target.value)}
            placeholder="e.g. 6mm frosted toughened · 4mm ACP · 12mm marine ply"
            className="h-9 w-full px-2 rounded-md bg-[#0B0F19] border border-slate-700 text-sm text-slate-100 focus:outline-none focus:border-cyan-500" />
          <datalist id="dl-panel-glass">{[...GLASS_PRESETS, ...INFILL_PRESETS].map((g) => <option key={g} value={g} />)}</datalist>
          <InfillChip spec={panelGlass || item.glass_spec} />
        </label>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {OPENINGS.map((o) => (
          <button key={o.value} data-testid={`opening-${o.value}`} onClick={() => setOp(o.value)}
            className={`p-2 rounded-md border flex flex-col items-center gap-1 transition-colors ${current === o.value ? "border-cyan-400 bg-cyan-500/10" : "border-slate-700 hover:border-slate-500 hover:bg-slate-800/60"}`}>
            <div className="h-12 w-10"><ElevationThumb item={iconItem(o.value)} size={40} /></div>
            <span className="text-[10px] leading-tight text-center text-slate-300">{o.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};
